import {
  SEVV_DIALOG_PAPER_BACKGROUND_SOLID,
  SEVV_DIALOG_PAPER_BORDER_DIVIDER,
  SEVV_DIALOG_PAPER_SHADOW,
  SEVV_DIALOG_TITLE_BACKGROUND,
  SEVV_DIALOG_TITLE_BORDER_BOTTOM,
  SEVV_DIALOG_TITLE_COLOR,
  sevvDialogTitleChromeSx,
} from '../../theme/sevvDialogChrome'

/**
 * Virtual Room dialogs — same shell as app `MuiDialog` / game menu.
 * Use **string px** for radii: MUI `sx` multiplies numeric `borderRadius` by `theme.shape.borderRadius`.
 */
const R = '8px' as const

export const SEVV_MODAL = {
  paper: {
    backgroundColor: SEVV_DIALOG_PAPER_BACKGROUND_SOLID,
    backgroundImage: 'none',
    borderRadius: R,
    border: SEVV_DIALOG_PAPER_BORDER_DIVIDER,
    boxShadow: SEVV_DIALOG_PAPER_SHADOW,
    minWidth: 360,
    maxWidth: 440,
    overflow: 'hidden',
    backdropFilter: 'blur(10px)',
  },
  backdrop: {
    backgroundColor: 'var(--sevv-overlay-backdrop, rgba(0, 0, 0, 0.5))',
    backdropFilter: 'blur(10px)',
  },
  title: {
    ...sevvDialogTitleChromeSx,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    /** Slightly more top than bottom so the chrome balances; bottom sits cleanly on the divider. */
    padding: '18px 24px 16px',
    fontFamily: "'Inter', 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif",
    fontWeight: 600,
    fontSize: '1.125rem',
  },
  content: {
    /** Extra top inset after the title bar so body copy is not tight to the divider. */
    padding: '28px 24px 24px',
  },
  actions: {
    padding: '16px 24px',
    backgroundColor: 'var(--sevv-dialog-actions-bg)',
    borderTop: 'var(--sevv-dialog-actions-border-top, 1px solid var(--sevv-divider))',
    /** `gap: 8` in sx is theme.spacing(8) — too wide; literal px between buttons */
    gap: '8px',
  },
  btnPrimary: {
    backgroundColor: 'var(--sevv-primary, #f97415)',
    color: '#ffffff !important',
    border: 'none',
    borderRadius: R,
    padding: '8px 16px',
    fontWeight: 600,
    boxShadow: 'var(--sevv-shadow-primary, 0 4px 14px 0 rgba(249, 116, 21, 0.3))',
    textTransform: 'none' as const,
    '&:hover': {
      backgroundColor: 'var(--sevv-primary-dark, #d65c05)',
      boxShadow: 'var(--sevv-shadow-primary-lg, 0 10px 25px 0 rgba(249, 116, 21, 0.4))',
      color: '#ffffff !important',
    },
    '&:disabled': {
      opacity: 0.6,
    },
  },
  btnSecondary: {
    background: 'var(--sevv-bg-paper, #ffffff)',
    color: 'var(--sevv-primary, #f97415)',
    border: '2px solid var(--sevv-primary, #f97415)',
    borderRadius: R,
    padding: '8px 16px',
    fontWeight: 600,
    textTransform: 'none' as const,
    '&:hover': {
      background: 'var(--sevv-primary-pale, #fff2d6)',
      borderColor: 'var(--sevv-primary-dark, #d65c05)',
    },
  },
  btnDanger: {
    color: 'var(--sevv-error, #e74c3c)',
    textTransform: 'none' as const,
    borderRadius: R,
    '&:hover': {
      backgroundColor: 'rgba(231, 76, 60, 0.08)',
    },
  },
  input: {
    '& .MuiOutlinedInput-root': {
      borderRadius: R,
      '&:hover fieldset': {
        borderColor: 'color-mix(in srgb, var(--sevv-primary, #f97415) 50%, transparent)',
      },
      '&.Mui-focused fieldset': { borderColor: 'var(--sevv-primary, #f97415)', borderWidth: 2 },
    },
  },
} as const
